import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';

const AdminInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadInvitations(); }, []);

  const loadInvitations = async () => {
    try {
      const { data, error } = await supabase
        .from('invitations').select('*').order('created_at', { ascending: false });
      if (!error && data) setInvitations(data);
    } catch (err) {
      console.error('Error loading invitations:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvitations = invitations.filter(inv => {
    const matchSearch = inv.recipient_name?.toLowerCase().includes(search.toLowerCase()) ||
      inv.occasion?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' ? true : inv.status === filter;
    return matchSearch && matchFilter;
  });

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'declined': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'opened': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Invitations</h2>
          <p className="text-gray-500 mt-1">{invitations.length} total invitations created</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[280px]">
          <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invitations..."
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none text-sm"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'opened', 'accepted', 'declined'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                filter === f
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Recipient</th>
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Occasion</th>
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="text-left px-8 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredInvitations.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-8 py-16 text-center">
                  <SafeIcon name="Mail" className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 font-medium">No invitations found</p>
                </td>
              </tr>
            ) : (
              filteredInvitations.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 bg-pink-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <SafeIcon name="Heart" className="text-pink-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{inv.recipient_name}</p>
                        <p className="text-xs text-gray-400">{inv.recipient_email || 'No email'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-sm text-gray-600 font-medium">{inv.occasion}</td>
                  <td className="px-8 py-5">
                    <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-600">
                      {inv.category}
                    </span>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${getStatusColor(inv.status)}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        inv.status === 'accepted' ? 'bg-green-500' :
                        inv.status === 'declined' ? 'bg-red-500' :
                        inv.status === 'pending' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`} />
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-sm text-gray-500">{formatDate(inv.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminInvitations;